// Payroll Settings — company-policy configuration in one place. Surfaces
// payroll-service config that previously had no UI: employer legal entities +
// statutory numbers, per-employee payroll profiles (statutory IDs, TRA
// classifiers, hire date, basic pay), terminal-dues policy, and payslip branding.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import CostBasisSetting from '../components/CostBasisSetting';
import { useSearchParams } from 'react-router-dom';
import { Building2, Users, Scale, Image as ImageIcon, Loader2, Plus, Save, CheckCircle2, Wallet, Trash2, Pencil, X, Clock, ShieldCheck, Landmark } from 'lucide-react';
import { OvertimeHoursTab } from '../components/OvertimeHoursTab';
import { PayScheduleTab } from '../components/PayScheduleTab';
import { ApprovalPolicyTab } from '../components/ApprovalPolicyTab';
import { PageBoundary } from '@/features/shared/components/PageBoundary';
import { RestrictedPayNotice } from '../components/RestrictedValue';
import { probePayAccess, type PayAccess } from '../lib/compensationAccess';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { gatewayApi } from '@/lib/hrApi';
import { useHasPermission } from '@/rbac/useRbac';
import { getApiErrorMessage } from '@/lib/apiError';
import { formatDate } from '../lib/format';
import {
  listAllowances, createAllowance, approveAllowance, endAllowance,
  getBenefitsSettings, updateBenefitsSettings, updateEmployee, type EmployeeAllowance,
} from '@/lib/hrApi';
import {
  listLegalEntities,
  createLegalEntity,
  updateLegalEntity,
  deleteLegalEntity,
  getEmployeeProfile,
  saveAssignment,
  setCompensation,
  getBranding,
  setBranding,
  getPayrollReadiness,
  bulkSetPayrollEnabled,
  assignEmployeesToLegalEntity,
  getLoanPolicy,
  setLoanPolicy,
  getEmployeeProfileSchema,
  type LegalEntity,
  type EmployeeProfile,
  type PayrollReadinessEmployee,
  type LoanPolicy,
} from '@/services/payrollSettingsService';
import { MoneyInput } from '@/features/shared/components/MoneyInput';
import { MaskedIdentifierInput } from '@/features/shared/components/MaskedIdentifierInput';
import {
  fieldList, isVisible, isRequired, validateField,
  type EmployeeProfileSchema, type IdentifierField, type ProfileContext,
} from '@/features/payroll/types/employeeProfileSchema';
import { Switch } from '@/features/shared/components/ui/switch';
import { Input } from '@/features/shared/components/ui/input';
import { listPayrollEmployees, getTerminalConfig, setTerminalConfig, type PayrollEmployeeLite, type TerminalConfig } from '@/services/terminalDuesService';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import { formatDate } from '../lib/format';
import { nudgeDana } from '@/features/shared/dana/danaNudge';

const fieldCls =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';
const labelCls = 'text-xs font-medium text-muted-foreground';

const COUNTRIES = [
  { code: 'TZ', label: 'Tanzania', currency: 'TZS' },
  { code: 'KE', label: 'Kenya', currency: 'KES' },
  { code: 'RW', label: 'Rwanda', currency: 'RWF' },
];

type TabId = 'entities' | 'roster' | 'employees' | 'schedule' | 'overtime' | 'terminal' | 'loans' | 'controls' | 'disbursement' | 'branding';
const TABS: { id: TabId; label: string; icon: typeof Building2 }[] = [
  { id: 'entities', label: 'Legal Entities', icon: Building2 },
  { id: 'roster', label: 'Payroll Roster', icon: Users },
  { id: 'employees', label: 'Employee Profiles', icon: Users },
  { id: 'schedule', label: 'Pay Schedule', icon: Clock },
  { id: 'overtime', label: 'Overtime & Hours', icon: Clock },
  { id: 'terminal', label: 'Terminal Dues Policy', icon: Scale },
  { id: 'loans', label: 'Loan & Advance Policy', icon: Wallet },
  { id: 'controls', label: 'Approval Policy', icon: ShieldCheck },
  { id: 'disbursement', label: 'Disbursement', icon: Landmark },
  { id: 'branding', label: 'Payslip Branding', icon: ImageIcon },
];

function Toast({ msg }: { msg: string | null }) {
  if (!msg) return null;
  return (
    <div className="flex items-center gap-2 rounded-md bg-success-soft px-3 py-2 text-sm text-success-strong">
      <CheckCircle2 className="h-4 w-4" /> {msg}
    </div>
  );
}

export const PayrollSettingsPage: React.FC = () => {
  const [params] = useSearchParams();
  const initialEmployee = params.get('employee') || '';
  const [tab, setTab] = useState<TabId>(
    (params.get('tab') as TabId) || (initialEmployee ? 'employees' : 'entities'),
  );
  // Deep-link from a legal entity → the Roster filtered to it ("add employees here").
  const [focusEntity, setFocusEntity] = useState('');
  const goToRoster = (entityId: string) => { setFocusEntity(entityId); setTab('roster'); };
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Payroll Settings</h1>
        <p className="text-sm text-muted-foreground">
          Company-policy configuration. Statutory tax rates (PAYE, NSSF, SDL, WCF) are set by the
          country pack and are not editable here.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                tab === t.id ? 'border-primary-500 text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'entities' && <LegalEntitiesTab onManageEmployees={goToRoster} />}
      {tab === 'roster' && <PayrollRosterTab initialEntity={focusEntity} />}
      {tab === 'employees' && <EmployeeProfilesTab initialEmployeeId={initialEmployee} />}
      {tab === 'schedule' && <PayScheduleTab />}
      {tab === 'overtime' && <OvertimeHoursTab />}
      {tab === 'terminal' && <TerminalPolicyTab />}
      {tab === 'loans' && <LoanPolicyTab />}
      {tab === 'controls' && (
        <PageBoundary compact title="Approval policy">
          <ApprovalPolicyTab />
        </PageBoundary>
      )}
      {tab === 'disbursement' && <DisbursementTab />}
      {tab === 'branding' && <BrandingTab />}
    </div>
  );
};

// ---------------------------------------------------------------- roster
// PayrollRosterTab — the one place to see who is on which legal entity and to
// pick who's included in payroll. Two concerns, one table:
//   • Legal entity — which employer a person is filed under. A run is created FOR
//     an entity and only pays that entity's employees, so an UNASSIGNED person is
//     invisible to every run. Select rows → "Assign to <entity>" to fix that in
//     bulk (statutory IDs/pay carry forward — nothing is wiped).
//   • Included — the payroll_enabled opt-out. Manual AND scheduled runs respect
//     it, so you set it once. Excluded people still show as auditable lines.
const UNASSIGNED = '__unassigned__';
const PayrollRosterTab: React.FC<{ initialEntity?: string }> = ({ initialEntity }) => {
  const confirm = useConfirm();
  const [emps, setEmps] = useState<PayrollReadinessEmployee[]>([]);
  const [entities, setEntities] = useState<LegalEntity[]>([]);
  const [enabled, setEnabled] = useState<Set<string>>(new Set());
  const [original, setOriginal] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  // Deep-linked from a legal entity → default the "assign to" target to it (and show
  // its unassigned candidates by filtering to Unassigned).
  const [entityFilter, setEntityFilter] = useState(initialEntity ? UNASSIGNED : ''); // '' = all, entity id, or UNASSIGNED
  const [assignTo, setAssignTo] = useState(initialEntity || '');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, ents] = await Promise.all([getPayrollReadiness(), listLegalEntities()]);
      setEmps(r.employees);
      setEntities(ents);
      const on = new Set(r.employees.filter((e) => e.payroll_enabled).map((e) => e.employee_id));
      setEnabled(new Set(on));
      setOriginal(new Set(on));
      setSelected(new Set());
      if (!assignTo && ents[0]) setAssignTo(ents[0].id);
    } catch (e) {
      setToast(getApiErrorMessage(e, 'Could not load the roster.'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const entityName = useMemo(() => {
    const m: Record<string, string> = {};
    entities.forEach((e) => (m[e.id] = e.name));
    return m;
  }, [entities]);
  const unassignedCount = useMemo(() => emps.filter((e) => !e.legal_entity_id).length, [emps]);

  const shown = useMemo(
    () =>
      emps.filter((e) => {
        if (!e.full_name.toLowerCase().includes(filter.trim().toLowerCase())) return false;
        if (entityFilter === UNASSIGNED) return !e.legal_entity_id;
        if (entityFilter) return e.legal_entity_id === entityFilter;
        return true;
      }),
    [emps, filter, entityFilter],
  );
  const allShownSelected = shown.length > 0 && shown.every((e) => selected.has(e.employee_id));

  const toggleEnabled = (id: string) =>
    setEnabled((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const toggleSelect = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const toggleSelectAll = () =>
    setSelected((s) => {
      const n = new Set(s);
      if (allShownSelected) shown.forEach((e) => n.delete(e.employee_id));
      else shown.forEach((e) => n.add(e.employee_id));
      return n;
    });

  const added = emps.filter((e) => enabled.has(e.employee_id) && !original.has(e.employee_id));
  const removed = emps.filter((e) => !enabled.has(e.employee_id) && original.has(e.employee_id));
  const dirty = added.length + removed.length;

  const save = async () => {
    if (!dirty) return;
    const parts = [
      added.length ? `add ${added.length} employee${added.length === 1 ? '' : 's'} to` : '',
      removed.length ? `remove ${removed.length} employee${removed.length === 1 ? '' : 's'} from` : '',
    ].filter(Boolean);
    const ok = await confirm({
      title: 'Update payroll roster?',
      message: `This will ${parts.join(' and ')} payroll. Every future scheduled run applies this roster. Continue?`,
      confirmLabel: 'Save roster',
      danger: removed.length > 0,
    });
    if (!ok) return;
    setSaving(true);
    try {
      const updates = [
        ...added.map((e) => ({ employee_id: e.employee_id, payroll_enabled: true })),
        ...removed.map((e) => ({ employee_id: e.employee_id, payroll_enabled: false })),
      ];
      const res = await bulkSetPayrollEnabled(updates);
      let msg = `Roster saved — ${res.updated} updated.`;
      if (res.skipped_terminated.length) {
        msg += ` ${res.skipped_terminated.length} terminated employee(s) skipped — reinstate in HR first.`;
      }
      setToast(msg);
      await load();
    } catch (e) {
      setToast(getApiErrorMessage(e, 'Could not save the roster.'));
    } finally {
      setSaving(false);
    }
  };

  const assign = async () => {
    if (!assignTo || selected.size === 0) return;
    const ids = Array.from(selected);
    const target = entityName[assignTo] || 'this entity';
    const ok = await confirm({
      title: 'Assign to legal entity?',
      message: `Move ${ids.length} employee${ids.length === 1 ? '' : 's'} onto ${target}? They'll be paid under that employer's registration from the next run. Statutory IDs and pay are kept.`,
      confirmLabel: `Assign to ${target}`,
    });
    if (!ok) return;
    setAssigning(true);
    try {
      const res = await assignEmployeesToLegalEntity(assignTo, ids);
      let msg = `Assigned ${res.assigned} to ${target}.`;
      if (res.skipped) msg += ` ${res.skipped} skipped (no payroll record yet).`;
      setToast(msg);
      await load();
    } catch (e) {
      setToast(getApiErrorMessage(e, 'Could not assign employees.'));
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-5 space-y-4">
          <div>
            <h2 className="text-lg font-medium text-foreground">Payroll Roster</h2>
            <p className="text-sm text-muted-foreground">
              Assign each person to a <strong>legal entity</strong> (the employer they're paid and filed
              under) and set who's <strong>included</strong> in payroll. A run pays one entity, so anyone
              left unassigned won't appear in any run.
            </p>
          </div>

          {loading ? (
            <div className="py-8 text-center">
              <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {unassignedCount > 0 && (
                <button
                  type="button"
                  onClick={() => setEntityFilter(UNASSIGNED)}
                  className="w-full rounded-md border border-warning bg-warning-soft px-3 py-2 text-left text-sm text-warning-strong"
                >
                  <strong>{unassignedCount}</strong> employee{unassignedCount === 1 ? ' is' : 's are'} not
                  assigned to a legal entity — they won't appear in any run. Click to show them.
                </button>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <input
                  className={fieldCls + ' max-w-[220px] py-1'}
                  placeholder="Filter by name…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
                <select
                  className={fieldCls + ' max-w-[220px] py-1'}
                  value={entityFilter}
                  onChange={(e) => setEntityFilter(e.target.value)}
                >
                  <option value="">All legal entities</option>
                  {entities.map((en) => (
                    <option key={en.id} value={en.id}>{en.name}</option>
                  ))}
                  <option value={UNASSIGNED}>— Unassigned —</option>
                </select>
                <span className={labelCls + ' ml-auto'}>
                  {enabled.size} of {emps.length} in payroll
                </span>
              </div>

              {/* Selection action bar — assign the selected employees to an entity. */}
              {selected.size > 0 && (
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-primary-500/30 bg-primary-500/5 px-3 py-2">
                  <span className="text-sm font-medium text-foreground">{selected.size} selected</span>
                  <span className="text-sm text-muted-foreground">→ assign to</span>
                  <select className={fieldCls + ' max-w-[200px] py-1'} value={assignTo} onChange={(e) => setAssignTo(e.target.value)}>
                    {entities.map((en) => (
                      <option key={en.id} value={en.id}>{en.name} ({en.country})</option>
                    ))}
                  </select>
                  <Button size="sm" onClick={assign} disabled={assigning || !assignTo}>
                    {assigning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Building2 className="mr-2 h-4 w-4" />}
                    Assign
                  </Button>
                  <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setSelected(new Set())}>
                    Clear
                  </button>
                </div>
              )}

              <div className="max-h-72 overflow-y-auto rounded-md border border-border divide-y divide-border">
                <div className="flex items-center gap-2 px-3 py-2 text-xs bg-muted/50 font-medium text-muted-foreground">
                  <input type="checkbox" checked={allShownSelected} onChange={toggleSelectAll} aria-label="Select all" />
                  <span className="flex-1">Employee</span>
                  <span className="w-40">Legal entity</span>
                  <span className="w-24 text-right">In payroll</span>
                </div>
                {shown.map((e) => (
                  <div key={e.employee_id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent">
                    <input
                      type="checkbox"
                      checked={selected.has(e.employee_id)}
                      onChange={() => toggleSelect(e.employee_id)}
                      aria-label={`Select ${e.full_name}`}
                    />
                    <span className="flex-1 truncate">{e.full_name}</span>
                    <span className="w-40 truncate">
                      {e.legal_entity_id ? (
                        <span className="text-muted-foreground">{entityName[e.legal_entity_id] || 'Unknown'}</span>
                      ) : (
                        <span className="rounded bg-warning-soft px-1.5 py-0.5 text-xs text-warning-strong">Unassigned</span>
                      )}
                    </span>
                    <div className="w-24 text-right">
                      <button
                        type="button"
                        onClick={() => toggleEnabled(e.employee_id)}
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          enabled.has(e.employee_id)
                            ? 'bg-success-soft text-success-strong'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {enabled.has(e.employee_id) ? 'Included' : 'Excluded'}
                      </button>
                    </div>
                  </div>
                ))}
                {shown.length === 0 && (
                  <div className="px-3 py-3 text-sm text-muted-foreground">No employees match.</div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Button onClick={save} disabled={saving || dirty === 0}>
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  {dirty ? `Save inclusion (${dirty} change${dirty === 1 ? '' : 's'})` : 'No changes'}
                </Button>
                {dirty > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {added.length > 0 && `+${added.length} included`}
                    {added.length > 0 && removed.length > 0 && ', '}
                    {removed.length > 0 && `−${removed.length} excluded`}
                  </span>
                )}
                {toast && <Toast msg={toast} />}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------- entities
const BLANK_LE = { name: '', country: 'TZ', employer_tin: '', nssf: '', sdl: '', wcf: '' };

const LegalEntitiesTab: React.FC<{ onManageEmployees?: (entityId: string) => void }> = ({ onManageEmployees }) => {
  const [list, setList] = useState<LegalEntity[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [unassigned, setUnassigned] = useState(0);
  const [form, setForm] = useState({ ...BLANK_LE });
  const [editId, setEditId] = useState<string | null>(null); // null = adding
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The entity whose inline "add employees" picker is open (opened right after
  // creating one, or via each row's "Manage employees").
  const [manageEntity, setManageEntity] = useState<{ id: string; name: string } | null>(null);
  const confirm = useConfirm();

  const reload = () => listLegalEntities().then((ents) => {
    setList(ents);
    if ((ents?.length ?? 0) === 0) nudgeDana({ key: 'legal_entities', message: 'No employer registration yet — want help setting up payroll?', question: 'How do I set up my company for payroll?' });
  }).catch(() => setList([]));
  // Headcount per entity (and unassigned) — so it's clear who's filed where.
  const reloadCounts = () => getPayrollReadiness().then((r) => {
    const c: Record<string, number> = {};
    let u = 0;
    r.employees.forEach((e) => { if (e.legal_entity_id) c[e.legal_entity_id] = (c[e.legal_entity_id] || 0) + 1; else u++; });
    setCounts(c);
    setUnassigned(u);
  }).catch(() => {});
  useEffect(() => { reload(); reloadCounts(); }, []);

  const startEdit = (e: LegalEntity) => {
    setError(null);
    setEditId(e.id);
    const sids = e.employer_statutory_ids || {};
    setForm({
      name: e.name, country: e.country || 'TZ', employer_tin: e.employer_tin || '',
      nssf: sids.NSSF || '', sdl: sids.SDL || '', wcf: sids.WCF || '',
    });
  };
  const cancelEdit = () => { setEditId(null); setForm({ ...BLANK_LE }); setError(null); };

  const save = async () => {
    setError(null);
    const name = form.name.trim();
    const tin = form.employer_tin.trim();
    if (!name) { setError('Registered name is required.'); return; }
    if (!tin) { setError('Employer TIN is required.'); return; }
    // Tanzania TIN is 9 digits (commonly shown as 123-456-789).
    const tinDigits = tin.replace(/\D/g, '');
    if (form.country === 'TZ' && tinDigits.length !== 9) {
      setError('Tanzania TIN must be 9 digits (e.g. 123-456-789).'); return;
    }
    // Statutory registration numbers, when entered, should be digits only.
    const numeric = /^[0-9]+$/;
    const sid: Array<[string, string]> = [['NSSF employer no.', form.nssf], ['SDL registration no.', form.sdl], ['WCF registration no.', form.wcf]];
    for (const [label, val] of sid) {
      if (val.trim() && !numeric.test(val.trim())) { setError(`${label} should contain digits only.`); return; }
    }
    setBusy(true);
    try {
      const body = {
        name,
        country: form.country,
        employer_tin: tin,
        employer_statutory_ids: { NSSF: form.nssf.trim(), SDL: form.sdl.trim(), WCF: form.wcf.trim() },
        base_currency: COUNTRIES.find((c) => c.code === form.country)?.currency,
      };
      if (editId) {
        await updateLegalEntity(editId, body);
        setToast('Legal entity updated');
      } else {
        const res = await createLegalEntity(body);
        setToast('Legal entity created — now add employees');
        if (res?.id) setManageEntity({ id: res.id, name }); // open the inline picker for the new entity
      }
      setTimeout(() => setToast(null), 2500);
      cancelEdit();
      reload();
    } catch (e: any) {
      setError(e?.response?.data?.message || `Could not ${editId ? 'update' : 'create'} legal entity.`);
    } finally { setBusy(false); }
  };

  const remove = async (e: LegalEntity) => {
    if (!(await confirm({ title: 'Remove legal entity', message: `Remove "${e.name}"? This can't be undone.`, confirmLabel: 'Remove', danger: true }))) return;
    setDeletingId(e.id);
    setError(null);
    try {
      await deleteLegalEntity(e.id);
      if (editId === e.id) cancelEdit();
      setToast('Legal entity removed'); setTimeout(() => setToast(null), 2500);
      reload();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not remove legal entity.');
    } finally { setDeletingId(null); }
  };

  return (
    <div className="space-y-4">
      {/* Multi-entity only — hides itself for a single-company tenant. */}
      <CostBasisSetting entityCount={list.length} canEdit />
      <Card>
        <CardContent className="p-5 space-y-3">
          <h2 className="text-sm font-semibold text-foreground">Employer registrations</h2>
          {unassigned > 0 && (
            <p className="text-xs text-warning">
              {unassigned} employee{unassigned === 1 ? '' : 's'} not yet assigned to an entity — assign them in the
              <strong> Payroll Roster</strong> tab so they appear in runs.
            </p>
          )}
          {list.length === 0 ? (
            <p className="text-sm text-muted-foreground">No legal entities yet. Add your employer registration below.</p>
          ) : (
            <div className="divide-y divide-border">
              {list.map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div className="min-w-0">
                    <span className="font-medium text-foreground">{e.name}</span>
                    <span className="ml-2 text-muted-foreground">{e.country} · {e.base_currency}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      · {counts[e.id] || 0} employee{(counts[e.id] || 0) === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-muted-foreground">TIN {e.employer_tin || '—'}</span>
                    <button type="button" onClick={() => setManageEntity({ id: e.id, name: e.name })}
                      className="inline-flex items-center gap-1.5 rounded-md border border-orange-200 dark:border-orange-500/30 px-2 py-1 text-xs font-medium text-orange-700 dark:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-500/[0.12]">
                      <Users className="w-3.5 h-3.5" /> Manage employees
                    </button>
                    <button type="button" onClick={() => startEdit(e)} aria-label={`Edit ${e.name}`}
                      className="p-1.5 rounded-[8px] text-muted-foreground hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-500/[0.12]">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => remove(e)} disabled={deletingId === e.id} aria-label={`Remove ${e.name}`}
                      className="p-1.5 rounded-[8px] text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/[0.12] disabled:opacity-50">
                      {deletingId === e.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {manageEntity && (
        <EntityEmployeeAssign
          entityId={manageEntity.id}
          entityName={manageEntity.name}
          onClose={() => setManageEntity(null)}
          onChanged={reloadCounts}
          onOpenRoster={onManageEmployees ? () => onManageEmployees(manageEntity.id) : undefined}
        />
      )}

      <Card>
        <CardContent className="p-5 space-y-4">
          <h2 className="text-sm font-semibold text-foreground">{editId ? 'Edit legal entity' : 'Add legal entity'}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <label className="space-y-1.5"><span className={labelCls}>Registered name</span>
              <input className={fieldCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label className="space-y-1.5"><span className={labelCls}>Country</span>
              <select className={fieldCls} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}>
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select></label>
            <label className="space-y-1.5"><span className={labelCls}>Employer TIN</span>
              <input className={fieldCls} value={form.employer_tin} onChange={(e) => setForm({ ...form, employer_tin: e.target.value })} /></label>
            <label className="space-y-1.5"><span className={labelCls}>NSSF employer no.</span>
              <input className={fieldCls} value={form.nssf} onChange={(e) => setForm({ ...form, nssf: e.target.value })} /></label>
            <label className="space-y-1.5"><span className={labelCls}>SDL registration no.</span>
              <input className={fieldCls} value={form.sdl} onChange={(e) => setForm({ ...form, sdl: e.target.value })} /></label>
            <label className="space-y-1.5"><span className={labelCls}>WCF registration no.</span>
              <input className={fieldCls} value={form.wcf} onChange={(e) => setForm({ ...form, wcf: e.target.value })} /></label>
          </div>
          {error && <p className="text-sm text-danger">{error}</p>}
          <div className="flex items-center gap-3">
            <Button onClick={save} disabled={busy}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : editId ? <Save className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
              {editId ? 'Save changes' : 'Add entity'}
            </Button>
            {editId && (
              <Button variant="outline" onClick={cancelEdit} disabled={busy}>
                <X className="mr-2 h-4 w-4" /> Cancel
              </Button>
            )}
            <Toast msg={toast} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// EntityEmployeeAssign — the inline "add employees to this entity" picker shown on
// the Legal Entities tab (opened right after creating one, or via "Manage
// employees"). Multi-select with select-all + single-select, a name filter, and an
// "unassigned only" toggle; picking people and assigning moves them onto the entity
// (statutory IDs/pay carry forward). The full Payroll Roster remains one click away.
const EntityEmployeeAssign: React.FC<{
  entityId: string;
  entityName: string;
  onClose: () => void;
  onChanged: () => void;
  onOpenRoster?: () => void;
}> = ({ entityId, entityName, onClose, onChanged, onOpenRoster }) => {
  const confirm = useConfirm();
  const [emps, setEmps] = useState<PayrollReadinessEmployee[]>([]);
  const [entityName2, setEntityName2] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');
  const [unassignedOnly, setUnassignedOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, ents] = await Promise.all([getPayrollReadiness(), listLegalEntities()]);
      setEmps(r.employees);
      const m: Record<string, string> = {};
      ents.forEach((e) => (m[e.id] = e.name));
      setEntityName2(m);
      setSelected(new Set());
    } catch (e) {
      setToast(getApiErrorMessage(e, 'Could not load employees.'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [entityId]);

  const alreadyHere = useMemo(() => emps.filter((e) => e.legal_entity_id === entityId).length, [emps, entityId]);
  // Candidates = everyone NOT already on this entity (optionally only the unassigned).
  const shown = useMemo(
    () => emps.filter((e) => {
      if (e.legal_entity_id === entityId) return false;
      if (unassignedOnly && e.legal_entity_id) return false;
      return e.full_name.toLowerCase().includes(filter.trim().toLowerCase());
    }),
    [emps, filter, unassignedOnly, entityId],
  );
  const allShownSelected = shown.length > 0 && shown.every((e) => selected.has(e.employee_id));
  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allShownSelected) shown.forEach((e) => n.delete(e.employee_id));
    else shown.forEach((e) => n.add(e.employee_id));
    return n;
  });

  const assign = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    const moving = shown.filter((e) => selected.has(e.employee_id) && e.legal_entity_id).length;
    const ok = await confirm({
      title: 'Add to legal entity?',
      message: `Add ${ids.length} employee${ids.length === 1 ? '' : 's'} to ${entityName}?${moving ? ` ${moving} will be moved from another entity.` : ''} Statutory IDs and pay are kept.`,
      confirmLabel: `Add to ${entityName}`,
    });
    if (!ok) return;
    setAssigning(true);
    try {
      const res = await assignEmployeesToLegalEntity(entityId, ids);
      let msg = `Added ${res.assigned} to ${entityName}.`;
      if (res.skipped) msg += ` ${res.skipped} skipped (no payroll record yet).`;
      setToast(msg);
      await load();
      onChanged();
    } catch (e) {
      setToast(getApiErrorMessage(e, 'Could not assign employees.'));
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Card className="border-orange-200 dark:border-orange-500/30">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Users className="h-4 w-4 text-orange-600" /> Add employees to {entityName}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">{alreadyHere} already assigned · pick people below and assign them.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input className={fieldCls + ' max-w-[220px] py-1'} placeholder="Filter by name…" value={filter} onChange={(e) => setFilter(e.target.value)} />
          <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <input type="checkbox" checked={unassignedOnly} onChange={(e) => setUnassignedOnly(e.target.checked)} /> Unassigned only
          </label>
        </div>

        {loading ? (
          <div className="py-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="max-h-72 overflow-y-auto rounded-md border border-border divide-y divide-border">
            <label className="flex items-center gap-2 px-3 py-2 text-sm bg-muted/50 font-medium">
              <input type="checkbox" checked={allShownSelected} onChange={toggleAll} /> Select all{filter || unassignedOnly ? ' shown' : ''} ({shown.length})
            </label>
            {shown.map((e) => (
              <label key={e.employee_id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent">
                <input type="checkbox" checked={selected.has(e.employee_id)} onChange={() => toggle(e.employee_id)} />
                <span className="flex-1 truncate">{e.full_name}</span>
                {e.legal_entity_id && (
                  <span className="text-[11px] text-muted-foreground">on {entityName2[e.legal_entity_id] || 'another entity'}</span>
                )}
              </label>
            ))}
            {shown.length === 0 && <div className="px-3 py-3 text-sm text-muted-foreground">No employees to add{unassignedOnly ? ' — uncheck “Unassigned only” to move people from another entity' : ''}.</div>}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={assign} disabled={assigning || selected.size === 0}>
            {assigning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            {selected.size ? `Add ${selected.size} to ${entityName}` : 'Select employees'}
          </Button>
          {onOpenRoster && (
            <button type="button" onClick={onOpenRoster} className="text-xs text-muted-foreground hover:text-foreground underline">Open full Payroll Roster →</button>
          )}
          {toast && <Toast msg={toast} />}
        </div>
      </CardContent>
    </Card>
  );
};

// --------------------------------------------------------------- employees
// SectionHeading — a small labelled divider between the four profile sections.
const SectionHeading: React.FC<{ title: string; hint?: string }> = ({ title, hint }) => (
  <div className="border-b border-border pb-1.5">
    <h3 className="text-sm font-semibold text-foreground">{title}</h3>
    {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
  </div>
);

// Resolve where a schema field's value lives on the profile. NIN → the dedicated
// national_id column; every other key → the statutory_ids map. (Passport maps to a
// dedicated employee-service column in P3; until then it round-trips through
// statutory_ids so non-resident data is never lost — see the redesign doc.)
const fieldIsNationalId = (f: IdentifierField) => f.mapsTo === 'national_id';

const EmployeeProfilesTab: React.FC<{ initialEmployeeId?: string }> = ({ initialEmployeeId }) => {
  const [employees, setEmployees] = useState<PayrollEmployeeLite[]>([]);
  const [entities, setEntities] = useState<LegalEntity[]>([]);
  const [employeeId, setEmployeeId] = useState('');
  const [p, setP] = useState<EmployeeProfile | null>(null);
  const [origBasic, setOrigBasic] = useState('');
  // basic_salary is omitted (null) for a viewer without compensation:view — and also when
  // nothing is on record. probePayAccess tells the two apart so a restricted salary is
  // never shown as an empty, editable "not set" field.
  const [payAccess, setPayAccess] = useState<PayAccess | 'checking'>('allowed');
  const latestProfileId = useRef('');
  const [effectiveFrom, setEffectiveFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [schema, setSchema] = useState<EmployeeProfileSchema | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    listPayrollEmployees().then(setEmployees).catch(() => setEmployees([]));
    listLegalEntities().then(setEntities).catch(() => setEntities([]));
  }, []);

  // The selected legal entity determines the country → which statutory pack drives
  // the identity fields. Fall back to TZ so the form is usable before assignment.
  const entity = useMemo(() => entities.find((e) => e.id === p?.legal_entity_id), [entities, p?.legal_entity_id]);
  const country = entity?.country || 'TZ';
  const currency = entity?.base_currency || COUNTRIES.find((c) => c.code === country)?.currency || 'TZS';

  // Load the pack-driven field schema for the resolved country. A 404 (no pack)
  // leaves schema=null and the form falls back to whatever keys the record carries.
  useEffect(() => {
    let alive = true;
    getEmployeeProfileSchema(country)
      .then((s) => { if (alive) setSchema(s); })
      .catch(() => { if (alive) setSchema(null); });
    return () => { alive = false; };
  }, [country]);

  const load = async (id: string) => {
    setEmployeeId(id); setP(null); setError(null); setShowErrors(false);
    latestProfileId.current = id;
    if (!id) return;
    try {
      const prof = await getEmployeeProfile(id);
      setP(prof);
      setOrigBasic(prof.basic_salary ?? '');
      if (prof.basic_salary == null) {
        setPayAccess('checking');
        const access = await probePayAccess(id);
        if (latestProfileId.current === id) setPayAccess(access);
      } else {
        setPayAccess('allowed');
      }
    } catch (e: any) {
      setError(getApiErrorMessage(e, 'No payroll profile yet for this employee.'));
    }
  };

  // Deep-link from a payroll run ("Manage allowances") pre-selects the employee.
  useEffect(() => {
    if (initialEmployeeId) load(initialEmployeeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEmployeeId]);

  const set = (patch: Partial<EmployeeProfile>) => setP((prev) => (prev ? { ...prev, ...patch } : prev));
  const setSid = (k: string, v: string) => setP((prev) => (prev ? { ...prev, statutory_ids: { ...prev.statutory_ids, [k]: v } } : prev));

  // Predicate context: the other answers a field's requiredWhen/visibleWhen key off.
  const ctx: ProfileContext = {
    residentialStatus: p?.residential_status,
    employmentType: p?.employment_type,
    heslbBeneficiary: p?.heslb_beneficiary,
  };

  const fields = fieldList(schema);
  const visibleFields = fields.filter((f) => isVisible(f, ctx));
  const getFieldValue = (f: IdentifierField) => (fieldIsNationalId(f) ? (p?.national_id || '') : (p?.statutory_ids?.[f.key] || ''));
  const setFieldValue = (f: IdentifierField, v: string) => (fieldIsNationalId(f) ? set({ national_id: v }) : setSid(f.key, v));

  // Per-field validation (normalized form). Only visible fields are enforced.
  const fieldErrors: Record<string, string | null> = {};
  for (const f of visibleFields) fieldErrors[f.key] = validateField(f, getFieldValue(f), ctx);
  const identityHasErrors = Object.values(fieldErrors).some(Boolean);

  // Salary effective date must not precede the hire date (P3 enforces this
  // server-side; here we block early with a clear message).
  const salaryChanged = !!p?.basic_salary && p.basic_salary !== origBasic;
  const effBeforeHire = salaryChanged && !!p?.hire_date && effectiveFrom < p.hire_date;
  const canSave = !!p && !identityHasErrors && !effBeforeHire;

  const save = async () => {
    if (!p) return;
    setShowErrors(true);
    if (!canSave) { setError('Please fix the highlighted fields before saving.'); return; }
    setError(null); setBusy(true);
    try {
      if (schema) {
        // P3b: statutory identity fields (those with an employeeField) are written
        // to employee-service (the HR system-of-record); payroll receives them via
        // events. Only fields WITHOUT an employeeField (e.g. HESLB) go to the payroll
        // assignment. National ID is therefore NOT sent to payroll here.
        const hrPatch: Record<string, string> = {};
        const payrollStat: Record<string, string> = {};
        for (const f of visibleFields) {
          if (f.employeeField) hrPatch[f.employeeField] = getFieldValue(f);
          else payrollStat[f.key] = getFieldValue(f);
        }
        if (Object.keys(hrPatch).length > 0) {
          await updateEmployee(p.employee_id, hrPatch as any);
        }
        await saveAssignment(p.employee_id, {
          legal_entity_id: p.legal_entity_id || undefined,
          statutory_ids: payrollStat,
          heslb_beneficiary: p.heslb_beneficiary,
          nature_of_employment: p.nature_of_employment,
          residential_status: p.residential_status,
          employment_type: p.employment_type,
          hire_date: p.hire_date || undefined,
        });
      } else {
        // No pack for this country: keep the legacy single-write behaviour.
        await saveAssignment(p.employee_id, {
          legal_entity_id: p.legal_entity_id || undefined,
          statutory_ids: p.statutory_ids,
          heslb_beneficiary: p.heslb_beneficiary,
          national_id: p.national_id,
          nature_of_employment: p.nature_of_employment,
          residential_status: p.residential_status,
          employment_type: p.employment_type,
          hire_date: p.hire_date || undefined,
        });
      }
      // Only write compensation when the basic actually changed (each write is a revision).
      if (salaryChanged) {
        await setCompensation(p.employee_id, {
          amount: p.basic_salary as string,
          currency,
          effective_from: effectiveFrom,
        });
        setOrigBasic(p.basic_salary as string);
      }
      setToast('Profile saved'); setTimeout(() => setToast(null), 2500);
    } catch (e: any) {
      setError(getApiErrorMessage(e, 'Could not save the profile.'));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
    <BulkAllowances employees={employees} />
    <Card>
      <CardContent className="p-5 space-y-5">
        <label className="space-y-1.5 block max-w-md">
          <span className={labelCls}>Employee</span>
          <select className={fieldCls} value={employeeId} onChange={(e) => load(e.target.value)}>
            <option value="">Select an employee…</option>
            {employees.map((e) => <option key={e.employee_id} value={e.employee_id}>{e.full_name}</option>)}
          </select>
        </label>

        {error && <p className="text-sm text-danger">{error}</p>}

        {p && (
          <>
            {/* A. Employment assignment ------------------------------------ */}
            <SectionHeading title="Employment" hint="Employer of record and how this person is engaged." />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <label className="space-y-1.5"><span className={labelCls}>Legal entity</span>
                <select className={fieldCls} value={p.legal_entity_id || ''} onChange={(e) => set({ legal_entity_id: e.target.value || null })}>
                  <option value="">— none —</option>
                  {entities.map((en) => <option key={en.id} value={en.id}>{en.name} ({en.country})</option>)}
                </select></label>
              <label className="space-y-1.5"><span className={labelCls}>Hire date</span>
                <input type="date" className={fieldCls} value={p.hire_date || ''} onChange={(e) => set({ hire_date: e.target.value })} /></label>
              <label className="space-y-1.5"><span className={labelCls}>Nature of employment</span>
                <select className={fieldCls} value={p.nature_of_employment} onChange={(e) => set({ nature_of_employment: e.target.value })}>
                  <option value="permanent">Permanent</option>
                  <option value="contract">Contract</option>
                  <option value="casual">Casual / Temporary</option>
                </select></label>
              <label className="space-y-1.5"><span className={labelCls}>Residential status</span>
                <select className={fieldCls} value={p.residential_status} onChange={(e) => set({ residential_status: e.target.value })}>
                  <option value="resident">Resident</option>
                  <option value="non_resident">Non-resident</option>
                </select></label>
              <label className="space-y-1.5"><span className={labelCls}>Employment type (PAYE)</span>
                <select className={fieldCls} value={p.employment_type} onChange={(e) => set({ employment_type: e.target.value })}>
                  <option value="primary">Primary employer</option>
                  <option value="secondary">Secondary employer</option>
                </select></label>
            </div>

            {/* B. Statutory identification (pack-driven) ------------------- */}
            <SectionHeading
              title="Statutory identification"
              hint={schema ? `Identity numbers required by ${country} statutory rules.` : 'Statutory identity numbers on record.'}
            />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {schema && visibleFields.map((f) => {
                const fieldId = `sid-${f.key}`;
                const helpId = `sid-${f.key}-help`;
                const err = showErrors ? fieldErrors[f.key] : null;
                const required = isRequired(f, ctx);
                return (
                  <label key={f.key} className="space-y-1.5" htmlFor={fieldId}>
                    <span className={labelCls}>
                      {f.label}{required && <span className="text-danger"> *</span>}
                      {f.verification?.capable && (
                        <span className="ml-1.5 align-middle rounded bg-muted px-1 py-0.5 text-[10px] font-normal text-muted-foreground">unverified</span>
                      )}
                    </span>
                    <MaskedIdentifierInput
                      field={f}
                      id={fieldId}
                      value={getFieldValue(f)}
                      onChange={(v) => setFieldValue(f, v)}
                      invalid={!!err}
                      describedBy={f.helpText ? helpId : undefined}
                    />
                    {err
                      ? <p className="text-xs text-danger">{err}</p>
                      : f.helpText && <p id={helpId} className="text-xs text-muted-foreground">{f.helpText}</p>}
                  </label>
                );
              })}
              {!schema && (
                <>
                  {/* Fallback (no pack for this country): edit whatever is on record. */}
                  <label className="space-y-1.5"><span className={labelCls}>National ID</span>
                    <input className={fieldCls} value={p.national_id} onChange={(e) => set({ national_id: e.target.value })} /></label>
                  {['TIN', 'NSSF', 'HESLB'].map((k) => (
                    <label key={k} className="space-y-1.5"><span className={labelCls}>{k}</span>
                      <input className={fieldCls} value={p.statutory_ids[k] || ''} onChange={(e) => setSid(k, e.target.value)} /></label>
                  ))}
                </>
              )}
              {/* HESLB beneficiary flag drives HESLB field visibility above. */}
              <label className="flex items-center gap-2 pt-6 text-sm text-foreground">
                <input type="checkbox" checked={p.heslb_beneficiary} onChange={(e) => set({ heslb_beneficiary: e.target.checked })} />
                HESLB beneficiary
              </label>
            </div>

            {/* C. Compensation -------------------------------------------- */}
            <SectionHeading title="Compensation" hint="Basic pay is effective-dated — a change creates a new revision, never overwriting history." />
            {payAccess === 'restricted' ? (
              <RestrictedPayNotice className="max-w-xl" />
            ) : payAccess === 'checking' ? (
              <p className="text-sm text-muted-foreground"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Checking pay access…</p>
            ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {payAccess === 'unknown' && (
                <p className="sm:col-span-2 lg:col-span-3 rounded-md bg-warning-soft px-3 py-2 text-xs text-warning-strong">
                  We couldn't confirm whether you can see this person's pay, so the salary may not be shown even if one is on record.
                </p>
              )}
              <label className="space-y-1.5" htmlFor="basic-salary"><span className={labelCls}>Basic salary (monthly)</span>
                <MoneyInput id="basic-salary" currency={currency} value={p.basic_salary || ''} onChange={(v) => set({ basic_salary: v })} />
              </label>
              <label className="space-y-1.5"><span className={labelCls}>Salary effective from</span>
                <input type="date" className={fieldCls} value={effectiveFrom} min={p.hire_date || undefined} onChange={(e) => setEffectiveFrom(e.target.value)} />
                {effBeforeHire && <p className="text-xs text-danger">Effective date can’t be before the hire date ({p.hire_date}).</p>}
              </label>
            </div>
            )}

            <div className="flex items-center gap-3 pt-1">
              <Button onClick={save} disabled={busy}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save profile
              </Button>
              {showErrors && !canSave && <span className="text-xs text-danger">Fix the highlighted fields to save.</span>}
              <Toast msg={toast} />
            </div>
          </>
        )}
      </CardContent>
    </Card>
    {employeeId && p && <EmployeeAllowances employeeId={employeeId} />}
    </div>
  );
};

const A_TYPES = ['transport', 'housing', 'meal', 'fuel', 'internet', 'airtime', 'other'];

// BulkAllowances — define an allowance once and add it to many selected employees
// (e.g. transport for all field staff). Loops the audited per-employee create; each
// lands as a draft to be approved. For an allowance that applies to EVERY employee,
// use Company Benefits instead.
const BulkAllowances: React.FC<{ employees: PayrollEmployeeLite[] }> = ({ employees }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [f, setF] = useState({ allowance_type: 'transport', name: '', amount: '', taxable: true, in_kind: false });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);

  const shown = useMemo(
    () => employees.filter((e) => e.full_name.toLowerCase().includes(filter.toLowerCase())),
    [employees, filter],
  );
  const toggle = (id: string) =>
    setPicked((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allShownPicked = shown.length > 0 && shown.every((e) => picked.has(e.employee_id));
  const toggleAll = () =>
    setPicked((prev) => {
      const n = new Set(prev);
      if (allShownPicked) shown.forEach((e) => n.delete(e.employee_id));
      else shown.forEach((e) => n.add(e.employee_id));
      return n;
    });

  const enable = async () => { setBusy(true); try { await updateBenefitsSettings({ allowances_enabled: true }); setDisabled(false); } finally { setBusy(false); } };

  const apply = async () => {
    if (picked.size === 0 || !f.name || !f.amount) return;
    setBusy(true); setResult(null);
    const ids = Array.from(picked);
    const outcomes = await Promise.allSettled(ids.map((id) =>
      createAllowance(id, { allowance_type: f.allowance_type, name: f.name, amount: Number(f.amount), taxable: f.taxable, in_kind: f.in_kind, frequency: 'monthly' }),
    ));
    if (outcomes.some((o) => o.status === 'rejected' && (o.reason?.response?.status === 403 || /not enabled|disabled/i.test(o.reason?.response?.data?.message || '')))) {
      setDisabled(true); setBusy(false); return;
    }
    const ok = outcomes.filter((o) => o.status === 'fulfilled').length;
    const failed = outcomes.length - ok;
    setResult(`Added to ${ok} employee${ok === 1 ? '' : 's'}${failed ? `, ${failed} failed` : ''} — draft, approve to apply.`);
    setPicked(new Set());
    setBusy(false);
  };

  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary-500" />
            <h2 className="text-sm font-semibold text-foreground">Bulk add allowance</h2>
          </div>
          <Button variant="outline" size="sm" onClick={() => setOpen((o) => !o)}>{open ? 'Hide' : 'Add to many employees'}</Button>
        </div>

        {open && (disabled ? (
          <div className="rounded-md border border-warning bg-warning-soft p-3 text-sm text-warning-strong">
            Allowances aren’t enabled for this company yet.
            <Button size="sm" className="ml-3" onClick={enable} disabled={busy}>{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Enable allowances</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Allowance definition */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <label className="space-y-1.5"><span className={labelCls}>Type</span>
                <select className={fieldCls} value={f.allowance_type} onChange={(e) => setF({ ...f, allowance_type: e.target.value })}>
                  {A_TYPES.map((tp) => <option key={tp} value={tp} className="capitalize">{tp}</option>)}
                </select></label>
              <label className="space-y-1.5"><span className={labelCls}>Name</span>
                <input className={fieldCls} value={f.name} placeholder="e.g. Field transport" onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
              <label className="space-y-1.5"><span className={labelCls}>Amount (monthly)</span>
                <input type="number" min="0" className={fieldCls} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></label>
              <div className="flex items-end gap-4">
                <label className="flex items-center gap-2 text-sm pb-2"><input type="checkbox" checked={f.taxable} onChange={(e) => setF({ ...f, taxable: e.target.checked })} /> Taxable</label>
                <label className="flex items-center gap-2 text-sm pb-2"><input type="checkbox" checked={f.in_kind} onChange={(e) => setF({ ...f, in_kind: e.target.checked })} /> In kind</label>
              </div>
            </div>

            {/* Employee picker */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className={labelCls}>Apply to ({picked.size} selected)</span>
                <input className={fieldCls + ' max-w-[200px] py-1'} placeholder="Filter…" value={filter} onChange={(e) => setFilter(e.target.value)} />
              </div>
              <div className="max-h-56 overflow-y-auto rounded-md border border-border divide-y divide-border">
                <label className="flex items-center gap-2 px-3 py-2 text-sm bg-muted/50 font-medium">
                  <input type="checkbox" checked={allShownPicked} onChange={toggleAll} /> Select all{filter && ' (filtered)'}
                </label>
                {shown.map((e) => (
                  <label key={e.employee_id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent">
                    <input type="checkbox" checked={picked.has(e.employee_id)} onChange={() => toggle(e.employee_id)} />
                    {e.full_name}
                  </label>
                ))}
                {shown.length === 0 && <div className="px-3 py-3 text-sm text-muted-foreground">No employees match.</div>}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button onClick={apply} disabled={busy || picked.size === 0 || !f.name || !f.amount}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />} Add to {picked.size || ''} employee{picked.size === 1 ? '' : 's'}
              </Button>
              {result && <Toast msg={result} />}
            </div>
            <p className="text-xs text-muted-foreground">Each allowance is created as a draft per employee — approve them (here or in the run) to include in payroll. To apply to <em>every</em> employee, use Company Benefits.</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// EmployeeAllowances — per-employee allowances (cash or benefit-in-kind) right in
// the payroll profile. Allowances live in the Benefits service (gated by an
// "allowances enabled" flag); if disabled we offer to switch it on inline.
const EmployeeAllowances: React.FC<{ employeeId: string }> = ({ employeeId }) => {
  const [rows, setRows] = useState<EmployeeAllowance[]>([]);
  const [disabled, setDisabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [f, setF] = useState({ allowance_type: 'transport', name: '', amount: '', taxable: true, in_kind: false });

  const load = () => {
    listAllowances(employeeId)
      .then((r) => { setRows(r); setDisabled(false); })
      .catch((e: any) => {
        if (e?.response?.status === 403 || /not enabled|disabled/i.test(e?.response?.data?.message || '')) setDisabled(true);
        else setRows([]);
      });
  };
  useEffect(() => { load(); }, [employeeId]);

  const enable = async () => {
    setBusy(true);
    try {
      await updateBenefitsSettings({ allowances_enabled: true });
      load();
    } catch (e) {
      setToast(getApiErrorMessage(e, 'Could not enable allowances.'));
      setTimeout(() => setToast(null), 4000);
    } finally { setBusy(false); }
  };

  const add = async () => {
    if (!f.name || !f.amount) return;
    setBusy(true);
    try {
      await createAllowance(employeeId, {
        allowance_type: f.allowance_type, name: f.name, amount: Number(f.amount),
        taxable: f.taxable, in_kind: f.in_kind, frequency: 'monthly',
      });
      setF({ allowance_type: 'transport', name: '', amount: '', taxable: true, in_kind: false });
      setToast('Allowance added (draft — approve to apply)'); setTimeout(() => setToast(null), 3000);
      load();
    } catch (e) {
      setToast(getApiErrorMessage(e, 'Could not add the allowance.'));
      setTimeout(() => setToast(null), 4000);
    } finally { setBusy(false); }
  };

  const act = async (p: Promise<any>) => {
    try {
      await p;
      load();
    } catch (e) {
      setToast(getApiErrorMessage(e, 'Could not update the allowance.'));
      setTimeout(() => setToast(null), 4000);
    }
  };

  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Wallet className="h-5 w-5 text-primary-500" />
          <h2 className="text-sm font-semibold text-foreground">Allowances for this employee</h2>
        </div>

        {disabled ? (
          <div className="rounded-md border border-warning bg-warning-soft p-3 text-sm text-warning-strong">
            Allowances aren’t enabled for this company yet.
            <Button size="sm" className="ml-3" onClick={enable} disabled={busy}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Enable allowances
            </Button>
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-1.5 font-medium">Name</th><th className="font-medium">Type</th>
                  <th className="font-medium text-right">Amount</th><th className="font-medium">Tax</th>
                  <th className="font-medium">Status</th><th />
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id} className="border-t border-border">
                    <td className="py-1.5 font-medium text-foreground">{a.name}</td>
                    <td className="capitalize text-muted-foreground">{a.allowance_type}</td>
                    <td className="text-right tabular-nums">{a.currency} {Number(a.amount).toLocaleString()}</td>
                    <td className="space-x-1">
                      {a.in_kind && <span className="rounded bg-purple-500/15 px-1.5 py-0.5 text-xs text-purple-600 dark:text-purple-300">in kind</span>}
                      <span className="text-xs text-muted-foreground">{a.taxable ? 'taxable' : 'non-tax'}</span>
                    </td>
                    <td className="capitalize text-muted-foreground">{a.status}</td>
                    <td className="text-right space-x-1">
                      {a.status === 'draft' && <Button size="sm" variant="outline" onClick={() => act(approveAllowance(a.id))}>Approve</Button>}
                      {a.status !== 'ended' && <Button size="sm" variant="ghost" onClick={() => act(endAllowance(a.id))}><Trash2 className="h-4 w-4" /></Button>}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={6} className="py-4 text-center text-muted-foreground">No allowances yet.</td></tr>}
              </tbody>
            </table>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 pt-2 border-t border-border">
              <label className="space-y-1.5"><span className={labelCls}>Type</span>
                <select className={fieldCls} value={f.allowance_type} onChange={(e) => setF({ ...f, allowance_type: e.target.value })}>
                  {A_TYPES.map((tp) => <option key={tp} value={tp} className="capitalize">{tp}</option>)}
                </select></label>
              <label className="space-y-1.5"><span className={labelCls}>Name</span>
                <input className={fieldCls} value={f.name} placeholder="e.g. Transport" onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
              <label className="space-y-1.5"><span className={labelCls}>Amount (monthly)</span>
                <input type="number" min="0" className={fieldCls} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></label>
              <div className="flex items-end gap-4">
                <label className="flex items-center gap-2 text-sm pb-2"><input type="checkbox" checked={f.taxable} onChange={(e) => setF({ ...f, taxable: e.target.checked })} /> Taxable</label>
                <label className="flex items-center gap-2 text-sm pb-2"><input type="checkbox" checked={f.in_kind} onChange={(e) => setF({ ...f, in_kind: e.target.checked })} /> In kind</label>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={add} disabled={busy || !f.name || !f.amount}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />} Add allowance
              </Button>
              <Toast msg={toast} />
              <p className="text-xs text-muted-foreground">New allowances start as draft — approve to include them in payroll.</p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------- loan & advance policy
const LoanPolicyTab: React.FC = () => {
  const [policy, setPolicy] = useState<LoanPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    getLoanPolicy()
      .then((p) => setPolicy(p))
      .catch(() => setPolicy(null))
      .finally(() => setLoading(false));
  }, []);

  const patch = (p: Partial<LoanPolicy>) => setPolicy((cur) => (cur ? { ...cur, ...p } : cur));

  const save = async () => {
    if (!policy) return;
    setSaving(true);
    try {
      const saved = await setLoanPolicy(policy);
      setPolicy(saved);
      setToast('Loan & advance policy saved.');
      setTimeout(() => setToast(null), 3000);
    } catch (e) {
      setToast(getApiErrorMessage(e, 'Could not save — you may not have permission.'));
      setTimeout(() => setToast(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground">Loading policy…</div>;
  if (!policy) return <div className="py-12 text-center text-muted-foreground">Loan policy is unavailable for this workspace.</div>;

  return (
    <Card>
      <CardContent className="max-w-2xl space-y-6 p-6">
        <div>
          <h3 className="text-base font-semibold text-charcoal dark:text-foreground">Deduction protection (minimum net pay)</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Loan and advance repayments will never reduce an employee's take-home pay below this floor.
            When a repayment would breach it, it's reduced this period and the balance carries forward.
            Set to 0 to turn the guardrail off.
          </p>
        </div>

        <div className="grid gap-2 sm:max-w-xs">
          <label className="text-sm font-medium text-charcoal dark:text-foreground">Minimum net pay (per period)</label>
          <Input
            type="number"
            min={0}
            value={policy.min_net_pay ?? '0'}
            onChange={(e) => patch({ min_net_pay: e.target.value })}
            placeholder="0"
          />
        </div>

        <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
          <div>
            <p className="text-sm font-medium text-charcoal dark:text-foreground">Allow advances below the floor</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              An advance is the employee's own earned pay. Enable this to let advances be recovered in full
              even if that takes net below the minimum. Loans are always protected by the floor.
            </p>
          </div>
          <Switch
            checked={policy.advances_bypass_min_net}
            onCheckedChange={(v) => patch({ advances_bypass_min_net: v })}
          />
        </div>

        <div className="grid gap-2 sm:max-w-xs">
          <label className="text-sm font-medium text-charcoal dark:text-foreground">Maximum advance amount (0 = no cap)</label>
          <Input
            type="number"
            min={0}
            value={policy.max_advance_amount ?? '0'}
            onChange={(e) => patch({ max_advance_amount: e.target.value })}
            placeholder="0"
          />
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Button onClick={save} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save policy
          </Button>
          {toast && <span className="text-sm text-muted-foreground">{toast}</span>}
        </div>
      </CardContent>
    </Card>
  );
};

// NOTE: Per-tenant payout provider configuration lives on the dedicated
// "Payment provider" page (Company settings → settings/payments,
// PaymentProviderPage.tsx), backed by paygate-service — that page is canonical.

// ---------------------------------------------------------------- terminal
const TerminalPolicyTab: React.FC = () => {
  const [entities, setEntities] = useState<LegalEntity[]>([]);
  const [entityId, setEntityId] = useState('');
  const [cfg, setCfg] = useState<TerminalConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => { listLegalEntities().then((l) => { setEntities(l); if (l[0]) load(l[0].id); }).catch(() => {}); }, []);

  const load = async (id: string) => {
    setEntityId(id); setCfg(null);
    if (!id) return;
    try { setCfg(await getTerminalConfig(id)); } catch { setCfg(null); }
  };

  const save = async () => {
    if (!cfg || !entityId) return;
    setBusy(true);
    try {
      const saved = await setTerminalConfig(entityId, {
        severance_days_per_year: cfg.severance_days_per_year,
        severance_daily_divisor: cfg.severance_daily_divisor,
        max_severance_years: cfg.max_severance_years,
        leave_payout_divisor: cfg.leave_payout_divisor,
        notice_months: cfg.notice_months,
      });
      setCfg(saved);
      setToast('Policy saved'); setTimeout(() => setToast(null), 2500);
    } finally { setBusy(false); }
  };

  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        <p className="text-sm text-muted-foreground">
          Severance & notice formulas for the Final Settlement calculator. Tanzania statutory defaults:
          7 days' basic wage per year of service (daily wage = basic ÷ 26), leave encashed ÷ 30, one month's notice.
        </p>
        <label className="space-y-1.5 block max-w-md">
          <span className={labelCls}>Legal entity</span>
          <select className={fieldCls} value={entityId} onChange={(e) => load(e.target.value)}>
            <option value="">Select…</option>
            {entities.map((en) => <option key={en.id} value={en.id}>{en.name} ({en.country})</option>)}
          </select>
        </label>
        {cfg && (
          <>
            {cfg.is_default && <p className="text-xs text-warning">Using statutory defaults — saving creates a custom policy for this entity.</p>}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <label className="space-y-1.5"><span className={labelCls}>Severance days / year</span>
                <input type="number" step="0.5" className={fieldCls} value={cfg.severance_days_per_year} onChange={(e) => setCfg({ ...cfg, severance_days_per_year: e.target.value })} /></label>
              <label className="space-y-1.5"><span className={labelCls}>Daily-wage divisor (days/month)</span>
                <input type="number" step="1" className={fieldCls} value={cfg.severance_daily_divisor} onChange={(e) => setCfg({ ...cfg, severance_daily_divisor: e.target.value })} /></label>
              <label className="space-y-1.5"><span className={labelCls}>Max severance years (0 = uncapped)</span>
                <input type="number" step="1" className={fieldCls} value={cfg.max_severance_years} onChange={(e) => setCfg({ ...cfg, max_severance_years: Number(e.target.value) })} /></label>
              <label className="space-y-1.5"><span className={labelCls}>Leave payout divisor (days/month)</span>
                <input type="number" step="1" className={fieldCls} value={cfg.leave_payout_divisor} onChange={(e) => setCfg({ ...cfg, leave_payout_divisor: e.target.value })} /></label>
              <label className="space-y-1.5"><span className={labelCls}>Notice months (pay in lieu)</span>
                <input type="number" step="0.5" className={fieldCls} value={cfg.notice_months} onChange={(e) => setCfg({ ...cfg, notice_months: e.target.value })} /></label>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={save} disabled={busy}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save policy
              </Button>
              <Toast msg={toast} />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------- branding
const BrandingTab: React.FC = () => {
  const [logo, setLogo] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const ok = useMemo(() => /^https?:\/\//.test(logo) || logo === '', [logo]);

  useEffect(() => { getBranding().then(setLogo).catch(() => {}); }, []);

  const save = async () => {
    setBusy(true);
    try { await setBranding(logo); setToast('Branding saved'); setTimeout(() => setToast(null), 2500); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <CardContent className="p-5 space-y-4 max-w-xl">
        <h2 className="text-sm font-semibold text-foreground">Payslip logo</h2>
        <p className="text-sm text-muted-foreground">A hosted image URL shown on generated payslip PDFs.</p>
        <label className="space-y-1.5 block"><span className={labelCls}>Logo URL</span>
          <input className={fieldCls} value={logo} placeholder="https://…/logo.png" onChange={(e) => setLogo(e.target.value)} /></label>
        {!ok && <p className="text-sm text-danger">Enter a valid http(s) URL.</p>}
        {logo && ok && <img src={logo} alt="logo preview" className="h-12 rounded border border-border bg-white p-1" />}
        <div className="flex items-center gap-3">
          <Button onClick={save} disabled={busy || !ok}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save branding
          </Button>
          <Toast msg={toast} />
        </div>
      </CardContent>
    </Card>
  );
};

export default PayrollSettingsPage;

// ---------------------------------------------------------------- disbursement
// DisbursementTab — pick the rail the tenant's payroll payout FILE is formatted
// for. It selects which provider-specific bank template the payroll run's filing
// file uses (M-Pesa bulk portal, NMB, CRDB, …). The generic "Disbursement
// review" file is provider-agnostic and unaffected. Effective from the start of
// the current month (mid-month changes still cover this month's run). Requires
// payroll:write (the backend gates the PUT on the payroll admin permission).
interface DisbursementProvider { code: string; name: string; blurb: string; }
const DISBURSEMENT_PROVIDERS: DisbursementProvider[] = [
  { code: '', name: 'Not set — country default', blurb: 'No provider chosen: the run uses the country default bank format (NMB).' },
  { code: 'MPESA', name: 'M-Pesa (Vodacom) — bulk disbursement', blurb: 'One file uploaded to the M-Pesa bulk portal; M-Pesa pays each destination bank/wallet.' },
  { code: 'NMB', name: 'NMB Bank', blurb: 'NMB Internet Banking bulk-upload format.' },
  { code: 'CRDB', name: 'CRDB Bank', blurb: 'CRDB payroll-upload format.' },
];

const DisbursementTab: React.FC = () => {
  const canEdit = useHasPermission('payroll:write');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState<string>('');   // saved bank_code ('' = not set)
  const [selected, setSelected] = useState<string>('');  // pending selection
  const [effectiveFrom, setEffectiveFrom] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    gatewayApi
      .get<{ configured: boolean; bank_code?: string; effective_from?: string }>(
        '/api/v1/payroll/settings/disbursement-bank',
      )
      .then((r) => {
        if (!active) return;
        const code = r.data.configured ? (r.data.bank_code ?? '') : '';
        setCurrent(code);
        setSelected(code);
        setEffectiveFrom(r.data.effective_from ?? null);
      })
      .catch((e) => active && setError(getApiErrorMessage(e, 'Could not load the disbursement provider.')))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const dirty = selected !== current;

  const save = async () => {
    setSaving(true);
    setToast(null);
    try {
      const r = await gatewayApi.put<{ bank_code?: string; effective_from?: string }>(
        '/api/v1/payroll/settings/disbursement-bank',
        { bank_code: selected },
      );
      setCurrent(selected);
      setEffectiveFrom(r.data.effective_from ?? effectiveFrom);
      setToast('Disbursement provider saved. It applies from the start of this month onward.');
    } catch (e) {
      setToast(getApiErrorMessage(e, 'Could not save the disbursement provider.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Card><CardContent className="flex items-center gap-2 p-6 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</CardContent></Card>;
  }

  return (
    <Card>
      <CardContent className="max-w-2xl space-y-5 p-6">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <Landmark className="h-5 w-5" /> Disbursement provider
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Which rail your payroll payout FILE is formatted for. The provider-specific file (below the
            generic "Disbursement review") is generated in this provider's format. The review file itself
            is provider-agnostic and never changes.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{error}</div>
        )}

        <div className="space-y-2">
          {DISBURSEMENT_PROVIDERS.map((p) => (
            <label
              key={p.code || 'none'}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                selected === p.code ? 'border-primary-500 bg-primary-500/5' : 'border-border hover:bg-muted/40'
              } ${!canEdit ? 'cursor-not-allowed opacity-70' : ''}`}
            >
              <input
                type="radio"
                name="disbursement-provider"
                className="mt-1"
                value={p.code}
                checked={selected === p.code}
                disabled={!canEdit || saving}
                onChange={() => setSelected(p.code)}
              />
              <span>
                <span className="flex items-center gap-2 font-medium text-foreground">
                  {p.name}
                  {current === p.code && (
                    <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">Current</span>
                  )}
                </span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{p.blurb}</span>
              </span>
            </label>
          ))}
        </div>

        {effectiveFrom && current && (
          <p className="text-xs text-muted-foreground">Current provider effective from {formatDate(effectiveFrom.slice(0, 10))}.</p>
        )}

        {toast && (
          <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm text-foreground">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" /> {toast}
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button onClick={save} disabled={!canEdit || !dirty || saving}>
            {saving ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Saving…</> : <><Save className="mr-1.5 h-4 w-4" /> Save provider</>}
          </Button>
          {!canEdit && <span className="text-xs text-muted-foreground">You need payroll admin rights to change this.</span>}
        </div>
      </CardContent>
    </Card>
  );
};
